const { USER_ROLE } = require("./constants");


/**
 * 
 * @param {Array<any>} firstArray 
 * @param {Array<any>} secondArray 
 * @returns {boolean}
 */
function hasIntersection(firstArray, secondArray) {
   return secondArray.some(role => firstArray.includes(role));
}


/**
 * 
 * @param {string} url 
 * @returns {Promise<string>}
 */
async function resolveShortenedUrl(url) {
   // Use fetch with the 'manual' redirect mode to get the redirected URL
   const response = await fetch(url, { method: 'HEAD', redirect: 'manual' });

   // Extract the 'Location' header which contains the resolved URL
   const resolvedURL = response.headers.get('Location');

   if (resolvedURL)
      return resolvedURL;
   return url;
   
}

function delay(millis) {
   return new Promise(resolve => setTimeout(resolve, millis));
}


/**
 * 
 * @param {number} money 
 * @param {"off"|"up"|"down"|undefined} roundType 
 * @returns {number}
 */
function roundMoney(money, roundType="off") {
   const inflated = money * 100 || 0;
   let rounded;

   switch (roundType) {
      case "down":
         rounded = Math.floor(inflated);
         break;
      case "up":
         rounded = Math.ceil(inflated);
         break;
      default:
         rounded = Math.round(inflated);
   }

   return rounded / 100;
}

function calculateMonthlyPayment(principal, annualRateInPercentage, tenureInMonths) {

   let monthlyPayment;

   if (annualRateInPercentage === 0) {
      monthlyPayment = principal / tenureInMonths;
   } else {
      const monthlyRate = annualRateInPercentage / 100 / 12;
      const powComponent = Math.pow(1 + monthlyRate, tenureInMonths);
      const numerator = principal * monthlyRate * powComponent;
      const denominator = powComponent - 1;
      monthlyPayment = numerator / denominator;
   }

   return monthlyPayment;
   
}

function _extractNumberFromURL(url, dRegex) {
	// Step 1: Remove everything up to and including "3d"
	let cleanedUrl = url.replace(dRegex, "");
	
	// Step 2: Remove everything after and including "!"
	cleanedUrl = cleanedUrl.replace(/!.*$/, "");
	
	// Step 3: Convert the remaining string to a number
	return parseFloat(cleanedUrl) || null
 }
 
function _getLat(url) {
	return _extractNumberFromURL(url, /.*3d/)
 }
 
function _getLong(url) {
	return _extractNumberFromURL(url, /.*4d/)
 }
 
function getLatLongFromGoogleMapsURL(url) {
	return {
	  lat: _getLat(url),
	  long: _getLong(url)
	}
}

function getCustomerFullName(customer) {
   return customer.is_company ? customer.name : `${customer.name} ${customer.surname}`;
}

function _countAtomicProducts(product) {

   const hasComposition = Array.isArray(product.composition) && product.composition.length > 0;
   const atomicProducts = [];
   
   if (hasComposition) {
      for (const entry of product.composition) {
         const { product, quantity } = entry;
         const products = _countAtomicProducts(product);
         const _products = new Array(quantity).fill(products).flat();
         atomicProducts.push(..._products);
      }
   } else {
      atomicProducts.push(product);
   }

   return atomicProducts;
}

function countAtomicProducts(product) {
   const products = _countAtomicProducts(product);
   const result = [];
   const map = new Map();

   for (const product of products) {
      const _id = String(product._id);
      let mapEntry = map.get(_id);

      if (!mapEntry) {
         mapEntry = {
            product,
            quantity: 0,
         }

         map.set(_id, mapEntry);
         result.push(mapEntry);
      }

      mapEntry.quantity++
   }

   return result;
}

function formatNationalId(nationalId) {
   nationalId = nationalId.replace(/-/g, '');
   return nationalId.slice(0, 2) + '-' + nationalId.slice(2, -2) + '-' + nationalId.slice(-2);
}

function getAgentFullName(agent) {
   return agent.is_person ? `${agent.name} ${agent.surname}` : agent.name;
}

function isGuest(roles) {
   return roles.length === 1 && roles[0] === USER_ROLE.GUEST;
}

/**
 * 
 * This function is used for creating generating same dates for subsequent
 * months. We rely on the base date instead of the previous date in the sequence
 * so that if a date false on a different month date (because of differents in month lengths),
 * the next one won't get affected by that
 * 
 * @param {Date} date base date
 * @param {object} opts
 * @param {number?} opts.x number of months after the base date
 * @param {Date?} opts.currentDate the date one month before the date to be generated
 * 
 * @returns {Date}
 */
function thisDateNextXMonths(date, opts) {

   let { x, currentDate } = opts;

   if (x === 0)
      return date;

   if (!x) {
      if (!currentDate)
         throw new Error(`opts.currentDate is required when opts.x is not provided`);

      // get X from currentDate
      const yearDiff = currentDate.getFullYear() - date.getFullYear();
      const monthDiff = currentDate.getMonth() - date.getMonth();
      const monthsDiff = yearDiff * 12 + monthDiff;

      x = monthsDiff + 1; // add one because we want the date one month after {currentDate}
   }

   const expectedMonth = (date.getMonth() + x) % 12;

   const nextXMonths = new Date(date);
   nextXMonths.setMonth(nextXMonths.getMonth() + x);

   while (nextXMonths.getMonth() !== expectedMonth) {
      nextXMonths.setDate(nextXMonths.getDate() - 1);
   }

   return nextXMonths;
   
}

/**
 * 
 * @param {number} amount 
 * @returns 
 */
function formatMoney(amount) {
   return roundMoney(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/**
 * 
 * @param {Date | string | number} date 
 * @param {Object} options
 * @param {boolean} options.time
 * 
 * @returns {string} 
 */
function formatDate(date, options={}) {

   if (!(date instanceof Date))
      date = new Date(date);

   let res = date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
   });

   if (options.time) {
      res += ' @ ' + date.toLocaleTimeString('en-GB', {
         hour: '2-digit',
         minute: '2-digit',
         second: '2-digit',
         hourCycle: 'h23',
      });
   }

   return res;

}

/**
 * 
 * @param {Date} d 
 * @param {'-'|'/'} seperator 
 */
function toYYYYMMDD(d=new Date(), seperator='-') {
   const yyyy = d.getFullYear();
   const mm = (d.getMonth() + 1).toString().padStart(2, '0');
   const dd = d.getDate().toString().padStart(2, '0');

   return [ yyyy, mm, dd ].join(seperator);
}


module.exports = {
   calculateMonthlyPayment,
   countAtomicProducts,
   delay,
   formatDate,
   formatMoney,
   formatNationalId,
   getAgentFullName,
   getCustomerFullName,
   getLatLongFromGoogleMapsURL,
   hasIntersection,
   isGuest,
   resolveShortenedUrl,
   roundMoney,
   thisDateNextXMonths,
   toYYYYMMDD
};